import { ImageIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { IStatusBadgeProps, ISceneThumbnailProps } from '@/@types/propertySceneCard';

export function StatusBadge({ status, type }: IStatusBadgeProps) {
  const variants: Record<
    string,
    { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
  > = {
    pending: { label: 'Pending', variant: 'outline' },
    voiceover_generating: { label: 'Generating...', variant: 'secondary' },
    video_generating: { label: 'Generating...', variant: 'secondary' },
    generating: { label: 'Generating...', variant: 'secondary' },
    completed: { label: 'Ready', variant: 'default' },
    failed: { label: 'Failed', variant: 'destructive' },
  };

  const tooltips: Record<string, Record<string, string>> = {
    voiceover: {
      pending: 'Not yet generated. Use the regenerate button to create.',
      voiceover_generating: 'Voiceover is being generated. This may take a moment.',
      generating: 'Voiceover is being generated. This may take a moment.',
      completed: 'Successfully generated and ready to use.',
      failed: 'Generation failed. Click regenerate to try again.',
    },
    video: {
      pending: 'Not yet generated. Use the regenerate button to create.',
      video_generating: 'Video clip is being generated. This may take a few minutes.',
      generating: 'Video clip is being generated. This may take a few minutes.',
      completed: 'Successfully generated and ready to use.',
      failed: 'Generation failed. Click regenerate to try again.',
    },
  };

  const config = variants[status] || variants.pending;
  const tooltip = tooltips[type][status] || tooltips[type].pending;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={config.variant} className="text-xs cursor-help">
          {type === 'voiceover' ? '\uD83C\uDFA4' : '\uD83C\uDFAC'} {config.label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export function SceneThumbnail({
  storageId,
  sceneType,
  onChangeClick,
  size,
  aspectRatio,
  showChangeButton = true,
}: ISceneThumbnailProps) {
  const imageUrl = useQuery(api.storage.getUrl, storageId ? { storageId } : 'skip');

  const sizeClasses =
    aspectRatio === '9:16'
      ? size === 'small'
        ? 'w-[135px] h-[240px]'
        : 'w-[270px] h-[480px]'
      : size === 'small'
        ? 'w-[240px] h-[135px]'
        : 'w-[480px] h-[270px]';

  return (
    <div className={cn('relative rounded-lg overflow-hidden bg-muted', sizeClasses)}>
      {imageUrl ? (
        <img src={imageUrl} alt={`Scene: ${sceneType}`} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="h-8 w-8 text-muted-foreground" />
        </div>
      )}
      {showChangeButton && (
        <Button
          variant="secondary"
          size="sm"
          className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs"
          onClick={onChangeClick}
        >
          Change
        </Button>
      )}
    </div>
  );
}
